import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter, Link} from 'react-router';
import {sprintf} from 'sprintf-js';
import moment from 'moment-timezone';

export const NEW_MESSAGE_NOTIFICATION = 1;
const UPCOMING_CONSULTATION_NOTIFICATION = 2;
const NEW_CONSULTATION_NOTIFICATION = 3;
const CANCELLED_CONSULTATION_NOTIFICATION = 4;
const IMAGING_DELETION_NOTIFICATION = 5;
const FAX_SEND_FAILED = 6;
const FAX_RECEIVED = 7;
const NEW_SERVICE_REQUEST = 8;
const SHARE_PATIENT_RECORD = 9;
const ASSIGN_TASK = 10;
const NEW_MEDICAL_FILE = 11;
const INCOMING_DOCUMENT = 12;
const NEW_PATIENT = 13;
const APPROVE_CONSULTATION = 14;
const DECLINE_CONSULTATION = 15;
const RESCHEDULE_CONSULTATION = 16;

const DATE_FORMAT = 'D MMM h:mm A';

import styles from './Notification.scss';
import DefaultaAvatar from 'assets/forms/default-avatar.svg';
import RichTextView from 'components/controls/RichTextView';
import {userRole} from 'components/helpers/Role';

class Notification extends Component {
    static propTypes = {
        onRead: PropTypes.func.isRequired,
        notification: PropTypes.object.isRequired
    };

    state = {
        isFullShow: false
    };

    timer = null;

    componentDidMount() {
        const {notification} = this.props;
        if (notification.type !== UPCOMING_CONSULTATION_NOTIFICATION)
            return;

        const now = moment().unix();
        const validTimestamp = moment(notification.validUpTo).unix();

        this.timer = setTimeout(this.unvalidateNotification, (validTimestamp - now) * 1000);
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
        this.timer = null;
    }

    unvalidateNotification = () => {
        const {onRead} = this.props;
        onRead();
    }

    render() {
        if (!this.isKnownType())
            return null;
        const {notification} = this.props;
        const {author, createdAt} = notification;
        const avatar = author.avatar ? author.avatar : DefaultaAvatar;
        const notificationTime = moment.unix(createdAt);
        const timeString = notificationTime.isSame(moment(), 'day')
            ? notificationTime.format('h:mm A')
            : notificationTime.format('D MMM');

        return (
            <div className={styles.container}>
                <div className={styles.pointWrapper}>
                    <div className={styles.point} />
                </div>
                <div className={styles.avatar} style={{backgroundImage: `url(${avatar})`}} />
                <div className={styles.textContainer}>
                    <div className={styles.title}>{this.getTitle()}</div>
                    <div className={styles.body}>{this.getBody()}</div>
                </div>
                <div className={styles.time}>{timeString}</div>
            </div>
        );
    }

    isKnownType() {
        const {notification} = this.props;

        return [
            NEW_CONSULTATION_NOTIFICATION,
            CANCELLED_CONSULTATION_NOTIFICATION,
            NEW_MESSAGE_NOTIFICATION,
            UPCOMING_CONSULTATION_NOTIFICATION,
            IMAGING_DELETION_NOTIFICATION,
            FAX_SEND_FAILED,
            FAX_RECEIVED,
            NEW_SERVICE_REQUEST,
            SHARE_PATIENT_RECORD,
            ASSIGN_TASK,
            NEW_MEDICAL_FILE,
            INCOMING_DOCUMENT,
            NEW_PATIENT,
            APPROVE_CONSULTATION,
            DECLINE_CONSULTATION,
            RESCHEDULE_CONSULTATION,
        ].includes(notification.type);
    }

    getTitle() {
        const {notification, role} = this.props;
        switch (notification.type) {
            case NEW_CONSULTATION_NOTIFICATION:
                return !role.isVma()
                    ? loc('new-consultation-title')
                    : sprintf(loc('new-vma-consultation'), notification.doctorFullName);
            case CANCELLED_CONSULTATION_NOTIFICATION:
                return loc('cancelled-consultation-title');
            case NEW_MESSAGE_NOTIFICATION:
                return this.getNewMessageTitle();
            case UPCOMING_CONSULTATION_NOTIFICATION:
                return this.getUpcomingConsultationTitle();
            case IMAGING_DELETION_NOTIFICATION:
                return loc('imaging-deletion-title');
            case FAX_SEND_FAILED:
                return loc('fax-send-failed-title');
            case FAX_RECEIVED:
                return loc('fax-received-title');
            case NEW_SERVICE_REQUEST:
                const {notification: {patientName}} = this.props;
                return sprintf(loc('new-service-request-title'), patientName);
            case SHARE_PATIENT_RECORD:
                const {entityType} = notification;
                return sprintf(loc(entityType === 'lab' ?
                    'share-patient-lab-result-title' :
                    'share-patient-record-title'
                ));
            case ASSIGN_TASK:
                return loc('assign-task-notification-title');
            case NEW_MEDICAL_FILE:
                return loc('new-medical-file-notification-title');
            case INCOMING_DOCUMENT:
                return loc('incoming-document-notification-title');
            case NEW_PATIENT:
                return loc('new-patient-notification-title');
            case APPROVE_CONSULTATION:
                return loc('approve-consultation-title');
            case DECLINE_CONSULTATION:
                return loc('decline-consultation-title');
            case RESCHEDULE_CONSULTATION:
                return loc('reschedule-consultation-title');
        }
    }

    getNewMessageTitle() {
        const {notification} = this.props;
        const {author} = notification;

        return (
            <span>
                {loc('new-message-title')}
                {author.role === userRole.PATIENT && notification.hasAccessToProfile
                    ? (
                        <button onClick={this.handleLinkClick(author.id)} className={styles.profileLink}>
                            {author.fullName}
                        </button>
                    )
                    : author.fullName
                }
            </span>
        );
    }

    handleLinkClick = (authorId) => () => {
        this.unvalidateNotification();
        this.props.router.push(`/profile/${authorId}?tab=3`);
    }

    getUpcomingConsultationTitle() {
        const {notification} = this.props;
        const {author} = notification;
        return sprintf(loc('upcoming-consultation-title'), author.firstName, author.lastName);
    }

    getBody() {
        const {notification} = this.props;

        switch (notification.type) {
            case NEW_CONSULTATION_NOTIFICATION:
                return this.getNewConsultationBody();
            case CANCELLED_CONSULTATION_NOTIFICATION:
                return this.getCancelledConsultationBody();
            case NEW_MESSAGE_NOTIFICATION:
                return this.getNewMessageBody();
            case UPCOMING_CONSULTATION_NOTIFICATION:
                return this.getUpcomingConsultationBody();
            case IMAGING_DELETION_NOTIFICATION:
                return this.getImagingDeletionBody();
            case FAX_SEND_FAILED:
                return this.getFaxSendFailedBody();
            case FAX_RECEIVED:
                return this.getFaxReceivedBody();
            case NEW_SERVICE_REQUEST:
                return this.getNewServiceRequestBody();
            case SHARE_PATIENT_RECORD:
                return this.getSharePatientRecordBody();
            case ASSIGN_TASK:
                return this.getAssignTaskBody();
            case NEW_MEDICAL_FILE:
                return this.getNewMedicalFileBody();
            case INCOMING_DOCUMENT:
                return this.getIncomingDocumentBody();
            case NEW_PATIENT:
                return this.getNewPatientBody();
            case APPROVE_CONSULTATION:
                return this.getApproveConsultationBody();
            case DECLINE_CONSULTATION:
                return this.getDeclineConsultationBody();
            case RESCHEDULE_CONSULTATION:
                return this.getRescheduleConsultationBody();
        }
    }

    getApproveConsultationBody = () => {
        const {notification: {author, startAt}} = this.props;

        const authorName = author.fullName;

        return sprintf(loc('approve-consultation-body'), authorName, moment(startAt).format('MM/DD/YYYY'));
    }

    getDeclineConsultationBody = () => {
        const {notification: {author, startAt}} = this.props;

        const authorName = author.fullName;

        return sprintf(loc('decline-consultation-body'), authorName, moment(startAt).format('MM/DD/YYYY'));
    }

    getRescheduleConsultationBody = () => {
        const {notification: {author, oldDate, newDate}} = this.props;

        const authorName = author.fullName;
        const format = 'hh:mm a MM/DD/YYYY';

        return sprintf(
            loc('reschedule-consultation-body'),
            authorName,
            moment(oldDate).format(format),
            moment(newDate).format(format),
        );
    }

    getNewPatientBody = () => {
        const {notification: {author, patientName, doctorName}, role} = this.props;

        const patient = <Link to={`/profile/${author.id}`} className={styles.link}>{patientName}</Link>;

        return (
            <div>
                {sprintf(loc(`new-patient-notification-body-${role.role}`), doctorName)}
                {patient}
            </div>
        )
    }

    getNewMedicalFileBody = () => {
        const {notification: {author}} = this.props;

        return (
            <div>
                <Link
                    to={`/profile/${author.id}`}
                    className={styles.link}
                >
                    {author.fullName}
                </Link>
                {loc('new-medical-file-notification-text')}
            </div>
        );
    }

    getIncomingDocumentBody = () => {
        const {notification: {fromName}} = this.props;
        return sprintf(loc('incoming-document-notification-body'), fromName);
    };

    getAssignTaskBody = () => {
        const {notification: {author}} = this.props;

        return (
            <div>
                {author.role === userRole.PATIENT
                    ? <Link
                        to={`/profile/${author.id}`}
                        className={styles.link}
                    >
                        {author.fullName}
                    </Link>
                    : author.fullName}
                {loc('assign-task-notification-text')}
            </div>
        );
    }

    getRecordTypePlural = (entityType) => {
        switch (entityType) {
            case 'lab': return 'labs';
            case 'imaging': return 'imaging';
            case 'note': return 'notes';
        }
    }

    getSharePatientRecordBody = () => {
        const {notification: {encounterId, entityType, fromName, fromId}} = this.props;
        const link = `/home?encounterId=${encounterId}&encounterTab=${this.getRecordTypePlural(entityType)}`;
        const linkFromName = (
            <a href={`/profile/${fromId}`} className={styles.link}>
                {fromName}
            </a>
        );

        const linkToItem = (
            <a href={link} className={styles.link}>
                {loc(entityType)}
            </a>
        );

        return (
            <div>
                {entityType === 'lab' ?
                    locJsxResolver(loc( 'share-patient-lab-result-body'), linkFromName, linkToItem) :
                    locJsxResolver(loc('share-patient-record-body'), linkFromName, linkToItem)}
            </div>
        );
    }

    getFaxReceivedBody = () => {
        const {notification} = this.props;
        const {from} = notification;

        return sprintf(loc('fax-received-body'), from);
    }

    getNewServiceRequestBody() {
        const {notification} = this.props;
        const {patientId, patientName, serviceName} = notification;

        return (
            <div>
                <a
                    href={`/profile/${patientId}`}
                    className={styles.link}
                >
                    {patientName}
                </a>
                &nbsp;
                {sprintf(loc('new-service-request-body'), serviceName)}
            </div>
        );
    }

    getNewConsultationBody() {
        const {notification} = this.props;
        const {author, startAt} = notification;
        const date = moment(startAt).format(DATE_FORMAT);
        const patientName = author.fullName;

        return (
            <div>
                <Link to={`/profile/${author.id}`} className={styles.link}>{patientName}</Link>
                {sprintf(loc('new-consultation-body'), date)}
            </div>
        );
    }

    getFaxSendFailedBody() {
        const {notification} = this.props;
        const {to, status} = notification;

        return sprintf(loc('fax-failed-notification-body'), to, loc(status));
    }

    getCancelledConsultationBody() {
        const {notification} = this.props;
        const {author, startAt, reason} = notification;
        const date = moment(startAt).format(DATE_FORMAT);
        const {isFullShow} = this.state;
        return (
            <div>
                {sprintf(loc('cancelled-consultation-body'), date, author.firstName, author.lastName)}
                &nbsp;
                {!isFullShow &&
                    <div className={styles.showMore}
                        onClick={this.toggleFullShow}>{loc('cancelled-show-more')}</div>}
                {
                    isFullShow && <RichTextView className={styles.reason}
                        onClick={this.toggleFullShow}>
                        {reason}
                    </RichTextView >}
            </div >
        );
    }

    getNewMessageBody() {
        const {notification} = this.props;
        const {text, filesAmount} = notification;

        const body = text ? text :
            filesAmount === 1 ? loc('message-notification-with-file') : loc('message-notification-with-files');
        return (<RichTextView className={styles.messageText}>{body}</RichTextView>);
    }

    getUpcomingConsultationBody() {
        const {notification} = this.props;
        const {author} = notification;
        return sprintf(loc('upcoming-consultation-body'), author.firstName, author.lastName);
    }

    getImagingDeletionBody() {
        const {notification} = this.props;
        const {author, imaging} = notification;
        return sprintf(loc('imaging-deletion-body'), author.firstName, author.lastName, imaging.name);
    }

    toggleFullShow = (e) => {
        e.preventDefault();
        this.setState({isFullShow: !this.state.isFullShow});
    }
}

export default withRouter(Notification);



// WEBPACK FOOTER //
// ./src/components/Notifications/Notification.js